import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import {
  ORDER_STATUS_OPTIONS,
  ORDER_STATUSES_EDITABLE,
  getStatusMeta,
  resolveColorInputToHex,
  toCurrency,
} from "./shopUtils";

export function VariantColorSwatch({ value }) {
  const hex = resolveColorInputToHex(value);
  if (!hex) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 14,
        height: 14,
        borderRadius: "50%",
        bgcolor: hex,
        border: "1px solid",
        borderColor: "divider",
        verticalAlign: "middle",
        mr: 0.5,
        flexShrink: 0,
      }}
    />
  );
}

export function VariantCell({ options }) {
  if (!Array.isArray(options) || !options.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        Standard
      </Typography>
    );
  }
  return (
    <Stack spacing={0.25}>
      {options.map((option) => (
        <Stack
          key={`${option.name}-${option.value}`}
          direction="row"
          spacing={0.5}
          alignItems="center"
        >
          <VariantColorSwatch value={option.value} />
          <Typography variant="body2">
            {option.name}: {option.value}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

export function LineItemsDetailTable({ lineItems }) {
  if (!Array.isArray(lineItems) || !lineItems.length) return null;
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Produkt</TableCell>
            <TableCell>Variante</TableCell>
            <TableCell align="center">Menge</TableCell>
            <TableCell align="right">Einzelpreis</TableCell>
            <TableCell align="right">Gesamt</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lineItems.map((lineItem, index) => (
            <TableRow
              key={`${lineItem.productId || lineItem.productName}-${
                lineItem.variantKey
              }-${index}`}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {lineItem.productName}
                </Typography>
              </TableCell>
              <TableCell>
                <VariantCell options={lineItem.variantOptions} />
              </TableCell>
              <TableCell align="center">{lineItem.quantity}</TableCell>
              <TableCell align="right">
                {toCurrency(lineItem.unitPrice)}
              </TableCell>
              <TableCell align="right">
                {toCurrency(lineItem.lineTotal)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function OrderActions({
  order,
  onOrderStatusChange,
  onCancel,
  onDelete,
  onEdit,
  runningAdminAction,
}) {
  const canEdit = ORDER_STATUSES_EDITABLE.has(order.status);

  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
      <FormControl size="small" sx={{ minWidth: 170 }}>
        <Select
          value={order.status}
          onChange={(event) =>
            onOrderStatusChange(order._id, event.target.value)
          }
        >
          {ORDER_STATUS_OPTIONS.map((status) => {
            const meta = getStatusMeta(status);
            return (
              <MenuItem key={status} value={status}>
                {meta.label}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {canEdit && onEdit && (
        <Button
          size="small"
          variant="contained"
          color="info"
          disabled={runningAdminAction}
          onClick={() => onEdit(order)}
          startIcon={
            <Iconify icon="mdi:pencil-outline" width={16} height={16} />
          }
        >
          Bearbeiten
        </Button>
      )}
      <Button
        size="small"
        variant="contained"
        color="warning"
        disabled={order.status === "canceled" || runningAdminAction}
        onClick={() => onCancel(order)}
        startIcon={<Iconify icon="mdi:cancel" width={16} height={16} />}
      >
        Stornieren
      </Button>
      <Button
        size="small"
        variant="contained"
        color="error"
        disabled={runningAdminAction}
        onClick={() => onDelete(order)}
        startIcon={
          <Iconify icon="eva:trash-2-outline" width={16} height={16} />
        }
      >
        Löschen
      </Button>
    </Stack>
  );
}
